import React from 'react';
import { Box, Monitor, Database, Plug } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const iconMap = {
  box: Box,
  monitor: Monitor,
  database: Database,
  plug: Plug
};

const Products = () => {
  const { content } = useLanguage();
  const { products, ui } = content;

  return (
    <section id="productos" className="py-20 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            {ui.products.title}
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            {ui.products.subtitle}
          </p>
        </div>

        {/* Products Grid - 3 column Layout */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {products.map((product) => (
            <div
              key={product.id}
              className="group relative bg-gradient-to-b from-gray-900 to-gray-950 border border-gray-800 rounded-2xl overflow-hidden hover:border-blue-600/50 transition-all duration-300"
            >
              {/* Badge */}
              <div className="absolute top-4 right-4 z-10">
                <span className="px-3 py-1 bg-blue-600/20 border border-blue-600/30 text-blue-400 text-xs font-semibold rounded-full">
                  {product.badge}
                </span>
              </div>

              {/* Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/50 to-transparent"></div>
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="flex items-start mb-4">
                  <div className="w-12 h-12 bg-blue-600/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    {(() => {
                      const Icon = iconMap[product.icon] || Box;
                      return <Icon className="text-blue-400" size={24} />;
                    })()}
                  </div>
                  <h3 className="text-2xl font-bold text-white">
                    {product.title}
                  </h3>
                </div>
                
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {product.description}
                </p>

                {/* Features */}
                <ul className="space-y-2">
                  {product.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-sm">
                      <svg
                        className="w-5 h-5 text-blue-400 mr-2 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hover glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Bottom Info */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 text-lg">
            <span className="text-blue-400 font-semibold">{ui.products.bottomLabel}</span> {ui.products.bottomText}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Products;
